import { Autocomplete, Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { Controller, type Control, type FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { Locale } from "src/api/locales/useLocales";
import { ConsentControlledSwitch } from "../ConsentControlledSwitch";
import { ConsentRichTextField } from "../ConsentRichTextField";
import { ConsentInputSkeleton } from "./ConsentFormShared";
import type {
    ConsentItemOption,
    ConsentItemVersionFormData,
    ConsentVersionLocalizedDraft,
} from "../utils/consentVersionModalModel";

type ConsentVersionGlobalFieldsProps = {
    control: Control<ConsentItemVersionFormData>;
    errors: FieldErrors<ConsentItemVersionFormData>;
    impactedUsersLabel: string;
    isArchivedVersion: boolean;
    isGlobalContentLoading: boolean;
    itemOptions: ConsentItemOption[];
    selectedConsentItem: unknown;
    selectedConsentItemLabel: string;
};

type ConsentVersionLocalizedFieldsProps = {
    consentVersionId?: number;
    control: Control<ConsentItemVersionFormData>;
    editingLocale: string;
    errors: FieldErrors<ConsentItemVersionFormData>;
    hasAnyLocalizedConsentVersionField: boolean;
    isArchivedVersion: boolean;
    isLocalizedContentFetching: boolean;
    isLocalizedContentLoading: boolean;
    localeOptions: Array<Locale & { code: string }>;
    mutationIsLoading: boolean;
    open: boolean;
    onEditingLocaleChange: (nextLocale: string) => void;
    onLocalizedDraftChange: (
        patch: Partial<Pick<ConsentVersionLocalizedDraft, "content" | "documentUrl" | "summary" | "title">>,
    ) => void;
};

export const ConsentVersionGlobalFields = ({
    control,
    errors,
    impactedUsersLabel,
    isArchivedVersion,
    isGlobalContentLoading,
    itemOptions,
    selectedConsentItem,
    selectedConsentItemLabel,
}: ConsentVersionGlobalFieldsProps) => {
    const { t } = useTranslation();

    return (
        <Stack gap={2}>
            <Box>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em" }}>
                    {t("settings.consent.form.global_title", "Global settings")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t(
                        "settings.consent.form.version_global_description",
                        "Shared across every language for this legal version.",
                    )}
                </Typography>
            </Box>
            {isArchivedVersion && (
                <Box
                    sx={(theme) => ({
                        px: 2,
                        py: 1.5,
                        borderRadius: theme.spacing(1.5),
                        border: `1px solid ${theme.palette.divider}`,
                        bgcolor: theme.palette.grey[50],
                    })}
                >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {t("settings.consent.version.archived_read_only_title", "Archived version")}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t(
                            "settings.consent.version.archived_read_only",
                            "Archived versions are read-only to preserve the audit trail.",
                        )}
                    </Typography>
                </Box>
            )}
            {isGlobalContentLoading ?
                <ConsentInputSkeleton label={t("settings.consent.form.loading_version", "Loading version data...")} />
            :   <Controller
                    name="consentItemIri"
                    control={control}
                    render={({ field }) => {
                        const selectedOption = itemOptions.find((item) => item["@id"] === field.value) ?? null;

                        return (
                            <Autocomplete
                                options={itemOptions}
                                value={selectedOption}
                                disabled={isArchivedVersion}
                                onChange={(_, option) => field.onChange(option?.["@id"] ?? "")}
                                getOptionLabel={(option) => `${option.name} · ${option.code}`}
                                isOptionEqualToValue={(option, value) => option["@id"] === value["@id"]}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={selectedConsentItemLabel}
                                        error={Boolean(errors.consentItemIri)}
                                        helperText={errors.consentItemIri?.message}
                                    />
                                )}
                            />
                        );
                    }}
                />
            }
            {isGlobalContentLoading ?
                <ConsentInputSkeleton
                    height={92}
                    label={t("settings.consent.form.loading_impact", "Loading re-consent impact...")}
                />
            : selectedConsentItem ?
                <Box
                    sx={(theme) => ({
                        px: 2,
                        py: 1.5,
                        borderRadius: theme.spacing(1.5),
                        border: `1px solid ${theme.palette.info.light}`,
                        bgcolor: theme.palette.info[50],
                    })}
                >
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {t("settings.consent.version.reconsent_impact", "Estimated impact")}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {impactedUsersLabel}
                    </Typography>
                </Box>
            :   null}
            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
                {isGlobalContentLoading ?
                    <>
                        <ConsentInputSkeleton
                            label={t("settings.consent.form.loading_version", "Loading version data...")}
                        />
                        <ConsentInputSkeleton
                            label={t("settings.consent.form.loading_version", "Loading version data...")}
                            width={190}
                        />
                    </>
                :   <>
                        <Controller
                            name="version"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    {...field}
                                    label={t("settings.consent.version.version", "Version")}
                                    error={Boolean(errors.version)}
                                    helperText={errors.version?.message}
                                    disabled={isArchivedVersion}
                                    fullWidth
                                />
                            )}
                        />
                        <Controller
                            name="published"
                            control={control}
                            render={({ field }) => (
                                <ConsentControlledSwitch
                                    checked={field.value}
                                    disabled={isArchivedVersion}
                                    label={t("settings.consent.version.published", "Published")}
                                    onChange={field.onChange}
                                    sx={{ alignSelf: "center", m: 0 }}
                                />
                            )}
                        />
                    </>
                }
            </Stack>
        </Stack>
    );
};

export const ConsentVersionLocalizedFields = ({
    consentVersionId,
    control,
    editingLocale,
    errors,
    hasAnyLocalizedConsentVersionField,
    isArchivedVersion,
    isLocalizedContentFetching,
    isLocalizedContentLoading,
    localeOptions,
    mutationIsLoading,
    onEditingLocaleChange,
    onLocalizedDraftChange,
    open,
}: ConsentVersionLocalizedFieldsProps) => {
    const { t } = useTranslation();

    return (
        <Stack gap={2}>
            <Box>
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: "0.08em" }}>
                    {t("settings.consent.form.localized_title", {
                        defaultValue: "Translated content ({{locale}})",
                        locale: editingLocale.toUpperCase(),
                    })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {t(
                        "settings.consent.form.localized_description",
                        "These fields change with the selected content language.",
                    )}
                </Typography>
            </Box>
            {localeOptions.length > 0 && (
                <TextField
                    select
                    label={t("settings.consent.locale.label", "Content language")}
                    value={editingLocale}
                    disabled={mutationIsLoading || isLocalizedContentFetching || isArchivedVersion}
                    onChange={(event) => onEditingLocaleChange(event.target.value)}
                    fullWidth
                >
                    {localeOptions.map((option) => (
                        <MenuItem key={option.code} value={option.code}>
                            {`${option.nativeName || option.name || option.code} (${option.code})`}
                        </MenuItem>
                    ))}
                </TextField>
            )}
            {consentVersionId && !isLocalizedContentLoading && !hasAnyLocalizedConsentVersionField && (
                <Typography variant="body2" color="text.secondary">
                    {t(
                        "settings.consent.version.translation_fallback",
                        "No dedicated translation exists for this language yet. Saving will create or update this translation on the same version.",
                    )}
                </Typography>
            )}
            {isLocalizedContentLoading ?
                <ConsentInputSkeleton
                    label={t("settings.consent.form.loading_content", "Loading translated content...")}
                />
            :   <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={t("settings.consent.version.title", "Version title")}
                            error={Boolean(errors.title)}
                            helperText={errors.title?.message}
                            disabled={isArchivedVersion}
                            onChange={(event) => {
                                field.onChange(event);
                                onLocalizedDraftChange({ title: event.target.value });
                            }}
                            fullWidth
                        />
                    )}
                />
            }
            <Controller
                name="summary"
                control={control}
                render={({ field }) => (
                    <ConsentRichTextField
                        editorKey={`consent-version-summary-${consentVersionId ?? "new"}-${editingLocale}-${open ? "open" : "closed"}`}
                        id="consent-version-summary"
                        label={t("settings.consent.version.summary", "Summary")}
                        error={Boolean(errors.summary)}
                        helperText={errors.summary?.message}
                        disabled={isArchivedVersion}
                        loading={isLocalizedContentLoading}
                        loadingLabel={t("settings.consent.form.loading_content", "Loading translated content...")}
                        minHeight={120}
                        onChange={(value) => {
                            field.onChange(value);
                            onLocalizedDraftChange({ summary: value });
                        }}
                        value={field.value}
                    />
                )}
            />
            <Controller
                name="content"
                control={control}
                render={({ field }) => (
                    <ConsentRichTextField
                        editorKey={`consent-version-content-${consentVersionId ?? "new"}-${editingLocale}-${open ? "open" : "closed"}`}
                        id="consent-version-content"
                        label={t("settings.consent.version.content", "Content")}
                        error={Boolean(errors.content)}
                        helperText={errors.content?.message}
                        disabled={isArchivedVersion}
                        loading={isLocalizedContentLoading}
                        loadingLabel={t("settings.consent.form.loading_content", "Loading translated content...")}
                        minHeight={220}
                        onChange={(value) => {
                            field.onChange(value);
                            onLocalizedDraftChange({ content: value });
                        }}
                        value={field.value}
                    />
                )}
            />
            {isLocalizedContentLoading ?
                <ConsentInputSkeleton
                    label={t("settings.consent.form.loading_content", "Loading translated content...")}
                />
            :   <Controller
                    name="documentUrl"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={t("settings.consent.version.document_url", "Document URL")}
                            error={Boolean(errors.documentUrl)}
                            helperText={errors.documentUrl?.message}
                            disabled={isArchivedVersion}
                            onChange={(event) => {
                                field.onChange(event);
                                onLocalizedDraftChange({ documentUrl: event.target.value });
                            }}
                            fullWidth
                        />
                    )}
                />
            }
            <Box>
                <Typography variant="body" color="text.secondary">
                    {t(
                        "settings.consent.version.publish_hint",
                        "Publishing this version makes it the source of truth for end-user consent flows and automatically archives older sibling versions.",
                    )}
                </Typography>
            </Box>
        </Stack>
    );
};
